/**
 * EncuestaLauncher — decide si mostrar la encuesta post-login.
 *
 * Reglas:
 *  - Solo con encuesta activa para la audiencia y sin respuesta previa (DB).
 *  - "Responder después" pospone SNOOZE_MS vía localStorage (por encuesta+user),
 *    para no acosar en cada recarga.
 *  - Tras enviar, la fila en encuesta_respuestas evita que vuelva a aparecer
 *    en cualquier dispositivo.
 *
 * Montaje: dentro del AuthGate de cada app —
 *   dashboard: <EncuestaLauncher audiencia="cliente" />
 *   equipo:    <EncuestaLauncher audiencia="equipo" />
 */
import { useState, type ReactNode } from 'react';
import { useUser } from '@aosenuma/lib-supabase';
import type { EncuestaAudienciaRespondente } from '@aosenuma/lib-types';
import { EncuestaModal } from './EncuestaModal';
import { useEncuestaPendiente } from './useEncuestaPendiente';

const SNOOZE_MS = 24 * 60 * 60 * 1000; // 24 h

function snoozeKey(slug: string, userId: string): string {
  return `aos.encuesta.snooze.${slug}.${userId}`;
}

function isSnoozed(slug: string, userId: string): boolean {
  try {
    const raw = localStorage.getItem(snoozeKey(slug, userId));
    if (!raw) return false;
    const until = Number(raw);
    return Number.isFinite(until) && Date.now() < until;
  } catch {
    return false;
  }
}

function snooze(slug: string, userId: string): void {
  try {
    localStorage.setItem(snoozeKey(slug, userId), String(Date.now() + SNOOZE_MS));
  } catch {
    /* storage no disponible: se vuelve a mostrar en la próxima sesión */
  }
}

export interface EncuestaLauncherProps {
  audiencia: EncuestaAudienciaRespondente;
}

export function EncuestaLauncher({ audiencia }: EncuestaLauncherProps): ReactNode {
  const { user } = useUser();
  const { loading, encuesta, secciones, preguntas, yaRespondida } = useEncuestaPendiente(audiencia);
  const [dismissed, setDismissed] = useState(false);

  if (loading || dismissed || !user?.id || !encuesta || yaRespondida) return null;
  if (preguntas.length === 0) return null;
  if (isSnoozed(encuesta.slug, user.id)) return null;

  return (
    <EncuestaModal
      encuesta={encuesta}
      secciones={secciones}
      preguntas={preguntas}
      onPostpone={() => {
        snooze(encuesta.slug, user.id);
        setDismissed(true);
      }}
      onSubmitted={() => setDismissed(true)}
    />
  );
}
